import { useEffect, useRef, useState, type ReactNode } from 'react';
import { AlertTriangle, Check, Pencil, Search, X } from 'lucide-react';

import { useNetwork, type NetworkContextValue } from '../../lib/network-context';
import type { Network } from '../../hooks/useNetworkConfig';

const NETWORK_OPTIONS: Array<{ value: Network; label: string }> = [
  { value: 'mainnet-beta', label: 'Mainnet' },
  { value: 'custom', label: 'Custom' },
];

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

interface NetworkBarProps {
  /** Optional slot — typically the wallet UI rendered to the right. */
  rightSlot?: ReactNode;
  /** Click handler for the search trigger; ⌘K also opens the palette globally. */
  onOpenSearch?: () => void;
}

/**
 * Warn when the SDK is not going to use the endpoint the bar is showing.
 *
 * The bar renders the playground's own state; requests follow the SDK's.
 * Those are two different things, and when they disagree the symptom is an
 * account that "does not exist" — so say so instead of showing a URL that
 * had nothing to do with the lookup.
 */
function EffectiveRpcNotice({ net }: { net: NetworkContextValue }) {
  const { effective, rpcUrl, rpcReady } = net;

  let message: string | null = null;
  if (effective.error) {
    message = effective.error;
  } else if (!rpcReady && effective.rpcUrl) {
    message = `No RPC URL set — the SDK still holds ${effective.rpcUrl}. Requests go there.`;
  } else if (effective.rpcUrl && rpcUrl && effective.rpcUrl !== rpcUrl) {
    message = `SDK is using ${effective.rpcUrl}, not the URL shown. Requests follow the SDK.`;
  }

  if (!message) return null;

  return (
    <span className="net-warning" role="status" title={message}>
      <AlertTriangle size={11} strokeWidth={1.8} />
      <span className="net-warning-text">{message}</span>
    </span>
  );
}

export function NetworkBar({ rightSlot, onOpenSearch }: NetworkBarProps) {
  const net = useNetwork();
  const isAtlasnet = net.network === 'atlasnet';

  return (
    <div className="netbar">
      <a
        className="netbar-brand"
        href="https://sly.rentals"
        target="_blank"
        rel="noopener noreferrer"
      >
        <span className="logo" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 12 L12 3 L21 12 L12 21 Z" stroke="currentColor" strokeWidth="1.5" />
            <path d="M7 12 L12 7 L17 12 L12 17 Z" fill="var(--cyan)" stroke="var(--cyan)" />
          </svg>
        </span>
        <span className="name">
          SRSLY <em>· playground</em>
        </span>
      </a>

      <div className="netbar-mid">
        <div className="net-pill" data-net={net.network}>
          {NETWORK_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              className={net.network === opt.value ? 'is-active' : ''}
              onClick={() => net.setNetwork(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {net.network === 'custom' ? (
          <CustomRpcControl net={net} />
        ) : (
          <span className="net-status">
            <span
              className="dot"
              style={
                isAtlasnet
                  ? { background: 'var(--amber)', boxShadow: '0 0 6px var(--amber)' }
                  : undefined
              }
            />
            <span className="url">{net.rpcUrl}</span>
          </span>
        )}

        <EffectiveRpcNotice net={net} />

        {onOpenSearch && (
          <button
            type="button"
            className="netbar-search"
            onClick={onOpenSearch}
            aria-label="Open search"
          >
            <Search size={12} strokeWidth={1.6} />
            <span className="netbar-search-label">Search</span>
            <kbd className="netbar-search-kbd">{IS_MAC ? '⌘K' : 'Ctrl K'}</kbd>
          </button>
        )}
      </div>

      <div className="netbar-right">{rightSlot}</div>
    </div>
  );
}

/**
 * Custom-RPC display + edit control. Empty state shows a "Set RPC URL"
 * button. Once set, the URL is shown read-only with a pencil to edit
 * later. Persistence is handled by `useNetworkConfig` (localStorage).
 */
function CustomRpcControl({ net }: { net: NetworkContextValue }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(net.customRpcUrl);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Resync the draft if the persisted value changes externally (e.g. on
  // hydration or when network is re-selected).
  useEffect(() => {
    if (!editing) setDraft(net.customRpcUrl);
  }, [net.customRpcUrl, editing]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const startEdit = () => {
    setDraft(net.customRpcUrl);
    setEditing(true);
  };

  const save = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    net.setCustomRpcUrl(trimmed);
    setEditing(false);
  };

  const cancel = () => {
    setDraft(net.customRpcUrl);
    setEditing(false);
  };

  // Empty state — no URL persisted yet. Show a single CTA.
  if (!net.customRpcUrl && !editing) {
    return (
      <button type="button" className="sly-btn" onClick={startEdit}>
        Set RPC URL
      </button>
    );
  }

  if (editing) {
    return (
      <div className="netbar-rpc-edit">
        <input
          ref={inputRef}
          className="netbar-rpc-input"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') cancel();
          }}
          placeholder="https://your-rpc.example/…"
          spellCheck={false}
          autoComplete="off"
        />
        <button
          type="button"
          className="sly-icon-btn"
          onClick={save}
          disabled={!draft.trim()}
          title="Save (Enter)"
          aria-label="Save"
        >
          <Check size={11} strokeWidth={1.8} />
        </button>
        {net.customRpcUrl && (
          <button
            type="button"
            className="sly-icon-btn"
            onClick={cancel}
            title="Cancel (Esc)"
            aria-label="Cancel"
          >
            <X size={11} strokeWidth={1.8} />
          </button>
        )}
      </div>
    );
  }

  return (
    <span className="net-status net-status-custom">
      <span className="dot" />
      <span className="url">{net.customRpcUrl}</span>
      {net.effective.programs && (
        <span
          className="net-programs"
          title="Program address set the SDK resolved from this chain's genesis hash"
        >
          {net.effective.programs}
        </span>
      )}
      <button
        type="button"
        className="sly-icon-btn"
        onClick={startEdit}
        title="Edit RPC URL"
        aria-label="Edit RPC URL"
      >
        <Pencil size={11} strokeWidth={1.5} />
      </button>
    </span>
  );
}
